import { AcFormProvider, AcTitle, AcTypography } from '@components'
import { LABELS } from '@constants'
import { SandwichSubscriptionForm } from '@partials/forms/sandwich-subscription/sandwich-subscription'
import { ISandwichSubscriptionPayload } from '@typings'
import { PublicFormsApi } from 'src/api/public-forms.api'
import { useIframeResize } from '@hooks/use-iframe-resize'
import { useEffect, useMemo, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import styles from './ac-sandwich-subscription.module.scss'

const WEEKDAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
] as const

const api = new PublicFormsApi()

export const AcSandwichSubscription = () => {
  useIframeResize()

  useEffect(() => {
    const app = document.getElementById('app')
    if (app) {
      app.style.background = 'none'
      return () => {
        app.style.background = ''
      }
    }
  }, [])

  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [daysError, setDaysError] = useState<string | null>(null)

  const form = useForm<ISandwichSubscriptionPayload>({
    defaultValues: {
      company_name: '',
      invoice_email: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      street: '',
      house_number: null,
      house_number_addition: null,
      postal_code: '',
      city: '',
      start_date: '',
      monday: null,
      tuesday: null,
      wednesday: null,
      thursday: null,
      friday: null,
      tac: false,
    },
  })

  const hasAtLeastOneDay = (data: ISandwichSubscriptionPayload) => {
    return WEEKDAY_KEYS.some(
      day => data[day] !== null && data[day] !== undefined
    )
  }

  const handleErrors = (errors: Record<string, string[]>) => {
    const errorKeys = Object.keys(errors)
    errorKeys.forEach(key =>
      form.setError(key as keyof ISandwichSubscriptionPayload, {
        type: 'custom',
        message: errors[key][0],
      })
    )
  }

  const onSubmit = async (data: FieldValues) => {
    setDaysError(null)

    const payload = data as ISandwichSubscriptionPayload
    if (!hasAtLeastOneDay(payload)) {
      setDaysError(LABELS.SELECT_AT_LEAST_ONE_DAY)
      return
    }

    setLoading(true)
    try {
      await api.submit_sandwich_subscription(payload)
      setSuccess(true)
    } catch (e: any) {
      if (e?.response?.status === 422 && e?.response?.data?.errors) {
        handleErrors(e.response.data.errors)
      } else {
        toast.error(e?.response?.data?.message || LABELS.SERVER_ERROR)
      }
    } finally {
      setLoading(false)
    }
  }

  const onFormSubmit = () => {
    form.handleSubmit(onSubmit)()
  }

  const renderSuccess = useMemo(
    () => (
      <div className={styles['ac-sandwich-subscription__success']}>
        <AcTitle level={2}>
          {LABELS.SANDWICH_SUBSCRIPTION_SUCCESS_TITLE}
        </AcTitle>
        <AcTypography>{LABELS.SANDWICH_SUBSCRIPTION_SUCCESS_TEXT}</AcTypography>
      </div>
    ),
    []
  )

  if (success) {
    return (
      <div className={styles['ac-sandwich-subscription']}>{renderSuccess}</div>
    )
  }

  return (
    <div className={styles['ac-sandwich-subscription']}>
      <AcFormProvider
        form={form}
        onSubmit={onFormSubmit}
        loading={loading}>
        <SandwichSubscriptionForm
          onSubmit={onFormSubmit}
          loading={loading}
          daysError={daysError}
        />
      </AcFormProvider>
    </div>
  )
}
